import { storage } from "../firebase";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { defaultAvatarByGender } from "./defaultAvatar";

function isHttpUrl(v) {
  return typeof v === "string" && /^https?:\/\//i.test(v);
}

function normalizeStoragePath(p) {
  if (!p || typeof p !== "string") return "";
  let x = p.trim();
  if (!x) return "";
  if (x.startsWith("/")) x = x.slice(1);
  try {
    if (x.includes("%2F") || x.includes("%2f")) {
      x = decodeURIComponent(x);
    }
  } catch (_) {}
  return x;
}

function storagePathFromValue(value) {
  if (!value || typeof value !== "string") return "";

  const v = value.trim();
  if (!v) return "";

  if (v.startsWith("gs://")) {
    const withoutScheme = v.slice(5);
    const slash = withoutScheme.indexOf("/");
    if (slash >= 0) return normalizeStoragePath(withoutScheme.slice(slash + 1));
    return "";
  }

  if (!isHttpUrl(v)) {
    return normalizeStoragePath(v);
  }

  if (!v.includes("firebasestorage.googleapis.com")) {
    return "";
  }

  try {
    const part = v.split("/o/")[1];
    if (!part) return "";
    return normalizeStoragePath(decodeURIComponent(part.split("?")[0] || ""));
  } catch {
    return "";
  }
}

function firstPhotoFromUser(user) {
  const photos = Array.isArray(user?.photos) ? user.photos : [];
  for (const p of photos) {
    const raw =
      typeof p === "string" ? p : p?.path || p?.url || p?.downloadURL || "";
    if (String(raw || "").trim()) return String(raw).trim();
  }
  return "";
}

/**
 * Priority: profileImage → photoURL → avatar → mainPhotoURL → photos[0]
 */
export function getProfilePhotoRawFromUser(user) {
  if (!user || typeof user !== "object") return "";

  const fromPhotos = firstPhotoFromUser(user);

  const candidates = [
    user.profileImage,
    user.photoURL,
    user.avatar,
    user.mainPhotoURL,
    fromPhotos,
  ];

  for (const c of candidates) {
    const s = typeof c === "string" ? c.trim() : "";
    if (s) return s;
  }

  return "";
}

/**
 * Resolve a single stored value to a display URL. Returns "" on failure.
 */
export async function resolveProfilePhotoUrlCore(raw) {
  if (!raw || typeof raw !== "string") return "";

  const v = raw.trim();
  if (!v) return "";

  if (v.startsWith("data:")) return v;

  if (isHttpUrl(v)) return v;

  try {
    const pathOrGs = storagePathFromValue(v) || v;
    if (!pathOrGs) return "";
    return await getDownloadURL(storageRef(storage, pathOrGs));
  } catch (e) {
    console.warn("resolveProfilePhotoUrl failed:", v, e?.message || e);
    return "";
  }
}

/**
 * Resolve raw profile photo value; uses gender fallback when empty or failed.
 */
export async function resolveProfilePhotoUrl(raw, gender) {
  const url = await resolveProfilePhotoUrlCore(raw);
  if (url) return url;
  return defaultAvatarByGender(gender);
}

export async function resolveProfilePhotoFromUser(user, gender) {
  const raw = getProfilePhotoRawFromUser(user);
  return resolveProfilePhotoUrl(raw, gender);
}
